import React, { useState } from 'react';
import { SalvButton, SalvProgressCircle, Size } from '@salvapp/ui-components';
import { InternalApiEntityType, InternalApiScreeningFlowDraft } from '../../../api-internal';
import './ScreeningFlows.scss';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import SalvPage from '../../../common/SalvPage';
import { getConfigurationUrl, InternalNavigation } from '../../../navigation/helper';
import { SearchParamKeys } from '../../../common/searchParamKeys';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuerySegments } from '../segments/segmentActions';
import { useQueryScreeningFlows } from './screeningFlowActions';
import { useQueryOrganisationFlowDrafts } from '../../fields/draft/screeningFlowDraftActions';
import { Flow } from './Flow';
import { applyFlowFilters, emptyFlowFilters, FlowFilters, ScreeningFlowFilters } from './ScreeningFlowFilters';
import ExternalLink from '../../../common/ExternalLink';
import Has from '../../../auth/Has';
import { CAN_MODIFY_SCREENABLE_FIELDS_CONFIGURATION } from '../../../auth/permissions';
import DataLoadingErrorFallback from '../../../common/errors/DataLoadingErrorFallback';
import { ConfigurationProduct } from '../../../settings/configuration/Configuration';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import { useMe } from '../../../organisation/useMe';

export const EmptySpaceFiller = () => (
  <>
    <div />
    <div />
    <div />
    <div />
  </>
);

const ScreeningFlows = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { features } = useMe();
  const isFlowDraftsEnabled = isFeatureEnabled(ORGANISATION_FEATURE.SCREENING_FLOW_DRAFTS, features);

  const entityType = searchParams.get(SearchParamKeys.entityType) as InternalApiEntityType;

  const {
    data: screeningFlows,
    isPending: isScreeningFlowsPending,
    isError: isScreeningFlowsError,
  } = useQueryScreeningFlows(entityType);

  const { data: segments, isPending: isSegmentsPending, isError: isSegmentsError } = useQuerySegments({ entityType });

  const { data: organisationDrafts } = useQueryOrganisationFlowDrafts(entityType, isFlowDraftsEnabled);

  const [filters, setFilters] = useState<FlowFilters>(emptyFlowFilters);

  const isPending = isScreeningFlowsPending || isSegmentsPending;
  const isError = isScreeningFlowsError || isSegmentsError;

  if (isError) {
    return <DataLoadingErrorFallback message={'Could not load resources for Screening Flows'} />;
  } else if (isPending) {
    return <SalvProgressCircle ariaLabel={'Loading resources for Screening Flows'} />;
  }

  const allDrafts = organisationDrafts ?? [];
  const visibleFlows = applyFlowFilters(screeningFlows, filters);

  const activeFlowIds = new Set(screeningFlows.map((flow) => flow.id!));
  const orphanDrafts = allDrafts.filter((draft) => !activeFlowIds.has(draft.flowId));

  const nonActiveFlowsLatestDrafts = Object.values(
    orphanDrafts.reduce<Record<string, InternalApiScreeningFlowDraft>>((acc, draft) => {
      const existing = acc[draft.flowId];
      if (!existing || draft.createdTime.getTime() > existing.createdTime.getTime()) {
        acc[draft.flowId] = draft;
      }
      return acc;
    }, {})
  );

  const visibleNonActiveFlowsLatestDrafts = applyFlowFilters(
    nonActiveFlowsLatestDrafts.map((draft) => draft.snapshot),
    filters
  );

  const pendingDraftCountByFlowId = allDrafts.reduce<Record<string, number>>((acc, draft) => {
    acc[draft.flowId] = (acc[draft.flowId] ?? 0) + 1;
    return acc;
  }, {});

  const goToNewFlow = (isDraft: boolean) => {
    navigate(
      {
        pathname: InternalNavigation.screeningFlowWizard,
        search: new URLSearchParams(searchParams).toString(),
      },
      { state: { isDraft } }
    );
  };

  const totalRows = visibleFlows.length + (isFlowDraftsEnabled ? visibleNonActiveFlowsLatestDrafts.length : 0);

  return (
    <SalvPage
      title={entityType === InternalApiEntityType.PERSON ? 'Person Screening Flows' : 'Transaction Screening Flows'}
      subTitle={
        isFlowDraftsEnabled
          ? 'Draft, test and publish screening flows. Drafts run in isolation against historical or synthetic data and never touch live traffic.'
          : undefined
      }
      breadcrumb={{
        title: 'Configuration',
        to: getConfigurationUrl(ConfigurationProduct.SCREENING),
      }}
      actions={
        <Has allPermissions={[CAN_MODIFY_SCREENABLE_FIELDS_CONFIGURATION]}>
          <ExternalLink title={'Guide to Flows'} link={'https://help.salv.com/en/articles/236437-screening-flows'} />
          {isFlowDraftsEnabled && (
            <SalvButton
              label='New draft flow'
              variant='secondary'
              size={Size.SMALL}
              onClick={() => goToNewFlow(true)}
              className={'m-l-16'}
            />
          )}
          <SalvButton
            label='New Flow'
            icon={faPlus}
            variant='primary'
            size={Size.SMALL}
            onClick={() => goToNewFlow(false)}
            className={'m-l-8'}
          />
        </Has>
      }
      className='screening-flows-container'
    >
      <div className={'screening-flows'} style={{ '--rows-count': totalRows } as React.CSSProperties}>
        <ScreeningFlowFilters flows={screeningFlows} segments={segments} value={filters} onChange={setFilters} />
        {visibleFlows.map((flow, index) => (
          <Flow
            key={flow.id}
            rowNr={index + 1}
            status='active'
            flow={flow}
            segments={segments}
            showButtonsAndId
            pendingDraftCount={isFlowDraftsEnabled ? pendingDraftCountByFlowId[flow.id!] : undefined}
            isFlowDraftsEnabled={isFlowDraftsEnabled}
          />
        ))}
        {isFlowDraftsEnabled &&
          visibleNonActiveFlowsLatestDrafts.map((draft, index) => {
            return (
              <Flow
                key={`draft-${draft.id}`}
                rowNr={visibleFlows.length + index + 1}
                flow={draft}
                segments={segments}
                status='draft'
                showButtonsAndId
                isFlowDraftsEnabled={isFlowDraftsEnabled}
              />
            );
          })}
        <EmptySpaceFiller />
      </div>
    </SalvPage>
  );
};

export default ScreeningFlows;
